import { useState } from "react";
import type { Locale } from "../../i18n/dictionary";
import { getDictionary } from "../../i18n/dictionary";
import "../../components/portal.css";

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="var(--brand-hover)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="3" /><path d="M3 7l9 6 9-6" />
    </svg>
  );
}

// ---- LinkSent ---------------------------------------------------------------

export interface LinkSentProps {
  locale: Locale;
  email: string;
  onResend: () => void;
  onDifferentEmail: () => void;
  /**
   * When given, the screen also offers the one-time code from the same email
   * (caregiver and admin portals). Resolves true when the code signed them in.
   */
  onVerifyCode?: (code: string) => Promise<boolean>;
}

/** Spaces and dashes are how people copy codes; Supabase sends 6–10 digits. */
export function normaliseCode(raw: string): string | null {
  const code = raw.replace(/[\s-]/g, "");
  return /^\d{6,10}$/.test(code) ? code : null;
}

/**
 * "Check your inbox" screen — shown after a magic link is sent.
 * Shared between service and admin portals.
 */
export function LinkSent({ locale, email, onResend, onDifferentEmail, onVerifyCode }: LinkSentProps) {
  const dict = getDictionary(locale);
  const c = dict.common;
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!onVerifyCode || checking) return;
    const clean = normaliseCode(code);
    if (!clean) { setCodeError(c.codeFormat); return; }
    setChecking(true);
    setCodeError(null);
    const ok = await onVerifyCode(clean).catch(() => false);
    setChecking(false);
    if (!ok) setCodeError(c.codeInvalid);
  }
  return (
    <div
      className="zp-root"
      style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 32, position: "relative", overflow: "hidden", fontFamily: "var(--font-body)", background: "var(--bg)", color: "var(--ink)" }}
    >
      <div style={{ position: "absolute", top: -160, insetInlineEnd: -120, width: 520, height: 520, borderRadius: "50%", background: "radial-gradient(circle, var(--sage-tint), transparent 70%)", filter: "blur(8px)", pointerEvents: "none" }} />

      <div style={{ position: "relative", width: "100%", maxWidth: 440, textAlign: "center" }}>
        <div style={{ width: 88, height: 88, borderRadius: "var(--r-lg)", background: "var(--brand-tint)", display: "grid", placeItems: "center", margin: "0 auto 22px" }}>
          <IconMail />
        </div>
        <h1 style={{ fontSize: 28, color: "var(--ink-deep)", margin: 0 }}>{c.checkInbox}</h1>
        <p style={{ color: "var(--ink-soft)", marginTop: 12, fontSize: 16, lineHeight: 1.6, margin: "12px 0 0" }}>
          We sent a link to{" "}
          <b style={{ color: "var(--ink)" }}>{email || "your inbox"}</b>.
          {" "}Tap it to sign in.
        </p>

        <button
          onClick={onResend}
          style={{ marginTop: 26, minHeight: 54, padding: "0 30px", border: 0, borderRadius: "var(--r-pill)", background: "var(--brand)", color: "#fff", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 17, cursor: "pointer", boxShadow: "var(--sh-1)" }}
        >
          {c.sendNewLink}
        </button>

        {onVerifyCode && (
          <form onSubmit={submitCode} style={{ marginTop: 28, paddingTop: 22, borderTop: "1px solid var(--line)", textAlign: "start" }}>
            <p style={{ color: "var(--ink-soft)", fontSize: 15, lineHeight: 1.5, margin: "0 0 12px" }}>{c.orUseCode}</p>
            <label htmlFor="zp-signin-code" style={{ display: "block", fontWeight: 600, fontSize: 15, marginBottom: 6 }}>{c.codeLabel}</label>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input
                id="zp-signin-code"
                value={code}
                onChange={(e) => { setCode(e.target.value); setCodeError(null); }}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={14}
                aria-invalid={codeError ? true : undefined}
                aria-describedby={codeError ? "zp-signin-code-error" : undefined}
                style={{ flex: "1 1 160px", minHeight: 52, padding: "0 16px", border: "2px solid var(--line-strong)", borderRadius: "var(--r-md)", background: "var(--surface)", color: "var(--ink)", fontFamily: "var(--font-body)", fontSize: 20, letterSpacing: 4 }}
              />
              <button
                type="submit"
                disabled={checking}
                style={{ minHeight: 52, padding: "0 22px", border: "1.5px solid var(--brand)", borderRadius: "var(--r-pill)", background: "transparent", color: "var(--brand-hover)", fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 16, cursor: checking ? "default" : "pointer" }}
              >
                {checking ? c.checkingCode : c.signInWithCode}
              </button>
            </div>
            {codeError && (
              <p id="zp-signin-code-error" role="alert" style={{ background: "var(--peach-tint)", border: "1px solid var(--peach)", borderRadius: "var(--r-md)", padding: "12px 16px", margin: "12px 0 0", color: "var(--peach-deep)", fontSize: 14.5, lineHeight: 1.5 }}>{codeError}</p>
            )}
          </form>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 18, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={onDifferentEmail}
            style={{ background: "transparent", border: 0, color: "var(--ink-soft)", fontFamily: "var(--font-body)", fontSize: 15, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
          >
            {c.useDifferentEmail}
          </button>
        </div>
      </div>
    </div>
  );
}
